// src/components/Sidebar.js
import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import {
  AccountBalanceWalletOutlined,
  Close,
  DashboardOutlined,
  GroupAddOutlined,
  GroupsOutlined,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  PeopleOutlined,
} from "@mui/icons-material";
import { getRoleLabel, getStoredUser, getUserRole, ROLES } from "../auth/roles";

export const ADMIN_MENU = [
  { label: "Home", icon: DashboardOutlined, path: "/home" },
  { label: "Create Group", icon: GroupAddOutlined, path: "/create-group" },
  { label: "Started Groups", icon: GroupsOutlined, path: "/started-groups" },
  { label: "join Group Request", icon: PeopleOutlined, path: "/group-request" },
];

export const MEMBER_MENU = [
  { label: "Home", icon: DashboardOutlined, path: "/home" },
  { label: "All Groups", icon: GroupsOutlined, path: "/all-groups" },
  {
    label: "Joined Groups",
    icon: AccountBalanceWalletOutlined,
    path: "/joined-groups",
  },
];

const SidebarLinks = ({ items, activePath, collapsed, onNavigate }) => (
  <Box component="nav" aria-label="Main navigation">
    {items.map(({ label, icon: Icon, path }) => {
      const isActive = activePath === path;
      return (
        <Button
          key={path}
          fullWidth
          title={collapsed ? label : undefined}
          onClick={() => onNavigate(path)}
          startIcon={<Icon />}
          sx={{
            justifyContent: collapsed ? "center" : "flex-start",
            minWidth: 0,
            px: collapsed ? 1 : 2,
            py: 1.25,
            mb: 0.75,
            color: isActive ? "#fff" : "#687284",
            backgroundColor: isActive ? "#52057b" : "transparent",
            borderRadius: 1.5,
            textTransform: "none",
            fontWeight: isActive ? 750 : 600,
            transition: "all .2s ease",
            "& .MuiButton-startIcon": {
              mr: collapsed ? 0 : 1.2,
              color: isActive ? "#fff" : "#9aa2b1",
            },
            "&:hover": {
              backgroundColor: isActive ? "#52057b" : "#f7f1fa",
              color: isActive ? "#fff" : "#52057b",
            },
          }}
        >
          {!collapsed && label}
        </Button>
      );
    })}
  </Box>
);

const Sidebar = ({
  role,
  activePath = "/home",
  onNavigate,
  mobileOpen,
  onClose,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const currentRole = role || getUserRole();
  const user = getStoredUser();
  const items = currentRole === ROLES.ADMIN ? ADMIN_MENU : MEMBER_MENU;
  const name = user?.fullName || user?.full_name || "Dajoo member";
  const initials = name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const content = (isMobile = false) => (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          mb: 3,
          px: collapsed && !isMobile ? 0 : 1,
        }}
      >
        <Avatar
          sx={{
            width: 38,
            height: 38,
            bgcolor: "#eadcf1",
            color: "#52057b",
            fontWeight: 800,
          }}
        >
          {initials || "D"}
        </Avatar>
        {(!collapsed || isMobile) && (
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 800, fontSize: 13 }}>
              {name}
            </Typography>
            <Typography sx={{ color: "#7a8394", fontSize: 11 }}>
              {getRoleLabel(currentRole)}
            </Typography>
          </Box>
        )}
      </Box>
      {(!collapsed || isMobile) && (
        <Typography
          sx={{
            color: "#9aa2b1",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: ".1em",
            px: 1.5,
            mb: 1.5,
            textTransform: "uppercase",
          }}
        >
          Workspace
        </Typography>
      )}
      <SidebarLinks
        items={items}
        activePath={activePath}
        collapsed={collapsed && !isMobile}
        onNavigate={onNavigate}
      />
    </>
  );

  return (
    <>
      {/* Desktop Sidebar - FIXED (Never Scrolls) */}
      <Box
        component="aside"
        sx={{
          width: collapsed ? 76 : 248,
          height: "calc(100dvh - 80px)",
          position: "fixed",
          top: 80,
          left: 0,
          zIndex: 100,
          boxSizing: "border-box",
          backgroundColor: "#fff",
          borderRight: "1px solid #e9ebf0",
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          p: 2,
          transition: "width .2s ease",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: collapsed ? "center" : "flex-end",
            mb: 2,
          }}
        >
          <IconButton
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            onClick={() => setCollapsed((value) => !value)}
            size="small"
          >
            {collapsed ? (
              <KeyboardDoubleArrowRight />
            ) : (
              <KeyboardDoubleArrowLeft />
            )}
          </IconButton>
        </Box>
        {content()}
        <Divider sx={{ mt: "auto", mb: 1 }} />
        <Typography
          sx={{
            color: "#a1a8b5",
            fontSize: 11,
            textAlign: collapsed ? "center" : "left",
            px: 1,
          }}
        >
          Dajoo
        </Typography>
      </Box>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 30,
            backgroundColor: "rgba(25, 27, 38, 0.35)",
            display: { xs: "block", md: "none" },
          }}
          onClick={onClose}
        >
          <Box
            component="aside"
            onClick={(event) => event.stopPropagation()}
            sx={{
              width: 288,
              height: "100dvh",
              backgroundColor: "#fff",
              p: 2.5,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <IconButton
                aria-label="Close navigation"
                onClick={onClose}
                sx={{ color: "#555" }}
              >
                <Close />
              </IconButton>
            </Box>
            {content(true)}
          </Box>
        </Box>
      )}
    </>
  );
};

export default Sidebar;
